/*
  - - - - - - - - - - - - - - - - - - - - - - -
  Support CRUD actions for the store.
  Wraps HTTP module and supports setting the result and firing notifications
  - - - - - - - - - - - - - - - - - - - - - - -
*/
import { EventBus } from '@/utils/event-bus';
import HTTP from '@/utils/http';
import i18n from '@/locales';

/*
  Copy properties from one object in to another
  Useful for transforming before sending
  And limiting the properties sent
  props - {} or [] representation of property names
  item - item to take values from
  @return - new object shape
*/
function propMapping(props, item) {
  let toSend = {};
  if (props) {
    if (Array.isArray(props)) {
      props.forEach((prop) => {
        toSend[prop] = item[prop]; // Copy attributes over from 1 object to the other
      });
    } else {
      Object.keys(props).forEach((prop) => {
        // Support key: value notation, even with nested dot notation.
        try {
          toSend[prop] = props[prop].split('.').reduce((o, i) => o[i], item);
        } catch (e) {} //eslint-disable-line
      });
    }
  } else {
    toSend = item;
  }
  return toSend;
}

export default function crudable() {
  return {
    state: {},
    mutations: {
      ADD(state, { added }) {
        state.results.unshift(added);
      },
      // Supports updating store.results array. Finds item based on id.
      UPDATE(state, { updated }) {
        // if (!Array.isArray(state.results)) {
        //   // console.error('Calling update on non-array'); // Added as no errors emitted here.
        // }
        state.results.forEach((item, index) => {
          if (updated.id === item.id) {
            state.results.splice(index, 1); // remove old item
            state.results.splice(index, 0, updated); // add updated one
          }
        });
      },
      // Remove item from array based on id
      REMOVE(state, toRemove) {
        state.results = state.results.filter(item => toRemove.id !== item.id);
      },
    },
    actions: {
      // Create a new resource on the server.
      // Returns empty Promise
      createPartialApplication({
        uriRoot, // /uriRoot/ part of the url. Noun being operated on
        include, // Include params. Part of the Signable API to specify object props to be returned
        name, // Pretty name for a single instance of the object. Used in notifications
        uriSuffix, // Supports /uriRoot/$id/update pattern. Should be removed when API updated
        transform, // Custom function applied to results of API before setting mutations
        props, // {} or [] of props to copy over from item to sending object
        suppressAllErrors = false, // additional option passed to HTTP
        suppressServerErrors = false, // additional option passed to HTTP
        suppressFormErrors = false, // additional option passed to HTTP
      }) {
        return ({ commit }, item) => {
          // Build an object to send to the API based on the props sent in during creation.
          const toSend = propMapping(props, item);
          return HTTP({
            method: 'post',
            url: `${uriRoot}/${uriSuffix || ''}`, // Allows optional uri suffix
            params: {
              include,
            },
            data: {
              ...toSend,
            },
            suppressAllErrors,
            suppressServerErrors,
            suppressFormErrors,
          })
          .then(response => (transform ? transform(response) : response))
          .then((response) => {
            commit('ADD', { added: response.data.data });
            EventBus.$emit('renderGlobalMessage', ({ message: i18n.t('message.successfullyCreated', { name: name || uriRoot }), icon: 'check', type: 'success' }));
            return response;
          });
        };
      },
      // Get a resource from the server
      // Returns empty Promise
      getPartialApplication({ uriRoot, include, transform }) {
        return ({ commit }, { id }) =>
          // Returns empty Promise. Used to listen for success / failure
          HTTP({
            url: `${uriRoot}${id ? '/' : ''}${id}`, // Allows optional id
            params: {
              include,
            },
          }).then(response => (transform ? transform(response) : response)).then((response) => {
            commit('SET', {
              updated: response.data.data,
            });
          });
      },
      // Update a resource on the server
      // Updates local store with value
      // Returns empty Promise
      updatePartialApplication({
        uriRoot,
        uriSuffix,
        name,
        props,
        isSingular,
        transform,
        include,
        suppressAllErrors = false, // additional option passed to HTTP
        suppressServerErrors = false, // additional option passed to HTTP
        suppressFormErrors = false, // additional option passed to HTTP
        emitGlobalMessage = true,
      }) {
        return ({ commit }, item) => {
          // Build an object to send to the API based on the props sent in during creation.
          const toSend = propMapping(props, item);
          // Supports updating single instance store or array results
          const actionName = isSingular ? 'SET' : 'UPDATE';
          // Returns empty Promise. Used to listen for success / failure
          return HTTP({
            method: 'put',
            url: `${uriRoot}/${item.id}${uriSuffix ? '/' : ''}${uriSuffix || ''}`,
            data: {
              ...toSend,
            },
            params: {
              include,
            },
            suppressAllErrors,
            suppressServerErrors,
            suppressFormErrors,
          }).then(response => (transform ? transform(response) : response)).then((response) => {
            commit(actionName, { // Set for single element / update for array item
              updated: response.data.data,
            });
            if (emitGlobalMessage) {
                EventBus.$emit('renderGlobalMessage', ({ message: i18n.t('message.successfullyUpdated', { name: name || uriRoot }), icon: 'check', type: 'success' }));
            }
          });
        };
      },
      // Delete a resource from the server
      // Updates local store
      // Returns empty Promise
      deletePartialApplication({
        uriRoot, uriSuffix, name, transform,
      }) {
        return ({ commit }, item) =>
          HTTP({
            method: 'delete',
            url: `${uriRoot}/${item.id}${uriSuffix ? '/' : ''}${uriSuffix || ''}`, // Allows optional uri suffix
          }).then(response => (transform ? transform(response) : response)).then(() => {
            commit('REMOVE', item);
            EventBus.$emit('renderGlobalMessage', ({ message: i18n.t('message.successfullyDeleted', { name: name || uriRoot }), icon: 'check', type: 'success' }));
          });
      },
    },
  };
}
